import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {Button, Checkbox, Chip, Grid, ListItemText, MenuItem, TextField, useTheme} from "@mui/material";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import AdvancedFilter from "../../component/AdvancedFilter";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import Stack from "@mui/material/Stack";
import {LibraryAddRounded} from "@mui/icons-material";
import {useAgGridLocale, useHasScope} from "../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import * as Yup from "yup";
import {useSubscriptionActions} from "../../service/useSubscriptionActions";
import {ID_DISMISSED, ID_DRAFT, ID_READY} from "./constants/SubscriptionsStatuses";
import {useSubscriptionColors} from "./constants/useSubscriptionColors";
import SubscriptionDetailModal from "./modals/SubscriptionDetailModal";
import {SubscriptionDetail} from "./constants/SubscriptionModels";
import {INTEGER_REG_EXP} from "../../component/Constants";
import EmptyState from "../../../../util/component/EmptyState";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import {formatDate} from "../../component/Utils";
import {DateTimePicker} from "@mui/x-date-pickers";

const SubscriptionListPage = () => {

    const {globalData} = useContext(GlobalContext);
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'subscription'});
    const {t: tg} = useTranslation('translation');
    const agGridLocale = useAgGridLocale();
    const gridRef = useRef<AgGridReact>(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const {getSubscriptionColors} = useSubscriptionColors();
    const {getSubscriptions, getCategoriesBySegmentCode} = useSubscriptionActions();
    const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionDetail>();
    const [openDetail, setOpenDetail] = useState(false);
    const hasScope = useHasScope;

    const filterDefaultValues = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name) || [],
        dealer: globalData?.user?.dealers?.map((d: any) => d.name) || [],
        status: [], category: [], name: '', id: '', sku: '', priority: '',
        createdDateFrom: null, createdDateTo: null, lastModifiedDateFrom: null, lastModifiedDateTo: null
    };

    const [filters, setFilters] = useState<any>(filterDefaultValues);
    const [searchInput, setSearchInput] = useState("");
    const [tableShouldUpdate, setTableShouldUpdate] = useState(false);
    const [categories, setCategories] = useState<any[]>([]);

    const statuses = [
        {
            value: ID_DRAFT,
            label: t("statuses." + ID_DRAFT),
            color: getSubscriptionColors(ID_DRAFT)
        },
        {
            value: ID_READY,
            label: t("statuses." + ID_READY),
            color: getSubscriptionColors(ID_READY)
        },
        {
            value: ID_DISMISSED,
            label: t("statuses." + ID_DISMISSED),
            color: getSubscriptionColors(ID_DISMISSED)
        }
    ];

    useEffect(() => {
        const fetchCategories = async () => {
            if (!filters.legalEntity || filters.legalEntity.length === 0) {
                setCategories([]);
                setFilters((prev: any) => ({ ...prev, category: [] }));
                return;
            }

            const tenantPromises = filters.legalEntity.map((tenantId: string) =>
                getCategoriesBySegmentCode(tenantId)
            );

            const results = await Promise.allSettled(tenantPromises);

            const allCategories = results.reduce((acc: any[], result) => {
                if (result.status === 'fulfilled' && result.value) {
                    return acc.concat(result.value);
                } else if (result.status === 'rejected') {
                    console.error("Failed to fetch categories for a tenant:", result.reason);
                }
                return acc;
            }, []);

            const uniqueCategoriesMap = new Map();
            allCategories.forEach(category => {
                if (!uniqueCategoriesMap.has(category.categoryCode)) {
                    uniqueCategoriesMap.set(category.categoryCode, category);
                }
            });

            const uniqueCategories = Array.from(uniqueCategoriesMap.values());
            setCategories(uniqueCategories);

            if(filters.category.length > 0) {
                const availableCategoryNames = uniqueCategories.map(c => c.categoryName);
                const validCategories = filters.category.filter((c: string) => availableCategoryNames.includes(c));
                if (validCategories.length !== filters.category.length) {
                    setFilters((prev: any) => ({ ...prev, category: validCategories }));
                }
            }
        };

        fetchCategories();
    }, [filters.legalEntity]);

    const columns: ColDef[] = [
        {
            field: "id",
            headerName: t('columns.id'),
            unSortIcon: true,
            tooltipField: "id",
            pinned: 'left',
            lockPosition: 'left',
            lockPinned: true,
            maxWidth: 160
        },
        {
            field: "tenantId",
            headerName: t('columns.legalEntityField'),
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            }
        },
        {
            field: "dealerId",
            headerName: t('columns.dealerField'),
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            }
        },
        {
            field: "name",
            headerName: t('columns.name'),
            unSortIcon: true,
            tooltipField: "name",
        },
        {
            field: "category",
            headerName: t('columns.category'),
            unSortIcon: true,
            cellRenderer: (params: any) => {
                if (!params.data) return '';
                const categoryName = params.data.category;
                const category = categories.find(c => c.categoryName === categoryName);
                return category ? category.categoryLabel || category.categoryName : categoryName;
            },
            tooltipValueGetter: (params: any) => {
                if (!params.data) return '';
                const categoryName = params.data.category;
                const category = categories.find(c => c.categoryName === categoryName);
                return category ? category.categoryLabel || category.categoryName : categoryName;
            },
        },
        /* {
             field: "priority",
             headerName: t('columns.priority'),
             unSortIcon: true,
             tooltipField: "priority",
         },*/
        {
            field: "sku",
            headerName: t('columns.sku'),
            unSortIcon: true,
            tooltipField: "sku",
        },
        {
            field: "createdDate",
            headerName: t('columns.createdDate'),
            unSortIcon: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "lastModifiedDate",
            headerName: t('columns.lastModifiedDate'),
            unSortIcon: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "status",
            headerName: t('columns.status'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            cellClass: "grid-cell-center",
            headerClass: "grid-header-center",
            unSortIcon: true,
            maxWidth: 160,
            cellRenderer: (params: any) => {
                return <Chip label={t('statuses.' + params.data.status.toUpperCase())}
                             sx={{backgroundColor: getSubscriptionColors(params.data.status.toUpperCase())}}/>
            }
        },
        {
            field: "_actions",
            headerName: '',
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            maxWidth: 160,
            cellRenderer: (params: any) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip type={ActionTypes.edit} callback={() => {
                            window.open(`/salesmarketing/subscriptionupdate/${params.data.id}`, "_blank");
                        }} auth={'SCOPE_SM_UPDATE_SUBSCRIPTION_PLAN'}/>
                        <ActionButtonWithTooltip type={ActionTypes.copy} callback={() => {
                            navigate(`/salesmarketing/subscriptioncreate/`, {state: {copy: params.data.id}});
                        }} auth={'SCOPE_SM_COPY_SUBSCRIPTION_PLAN'}/>
                        <ActionButtonWithTooltip type={ActionTypes.details} callback={() => {
                            setOpenDetail(true);
                            setSelectedSubscription(params.data);
                        }} auth={'SCOPE_SM_VIEW_SUBSCRIPTION_PLAN_DETAIL'}/>
                    </Stack>
                );
            }
        }
    ];

    function createDatasource(filters: any, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp = {...filters};
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                try {
                    let data = await getSubscriptions(filterTemp);
                    params.success({
                        rowData: data?.content,
                        rowCount: data?.totalElements
                    });
                    if (data.totalElements === 0) {
                        api.showNoRowsOverlay();
                    } else {
                        api.hideOverlay();
                    }
                } catch (e) {
                    params.fail();
                }
            }
        };
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        pagination: true,
        paginationPageSize: 10,
        cacheBlockSize: 10,
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: true,
            autoHeight: true,
        },
        columnDefs: columns,
        serverSideInfiniteScroll: true,
        localeText: agGridLocale,
        noRowsOverlayComponentParams: {title: t('noRows')}
    };

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);

    useEffect(() => {
        if (tableShouldUpdate) {
            gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
            setTableShouldUpdate(false);
        }
    }, [tableShouldUpdate]);

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);


    const toolbar = {
        renderActions: () =>
            <>{hasScope('SCOPE_SM_CREATE_SUBSCRIPTION_PLAN')?
                <Button fullWidth startIcon={<LibraryAddRounded/>} onClick={() => {
                    navigate('/salesmarketing/subscriptioncreate')
                }}>
                    {t('buttons.createSubscription')}
                </Button>:<></>
            }
            </>
    };

    const validationObj = {
        legalEntity: Yup.array().min(1, tg('validation.selectAtLeastOne')),
        status: Yup.array(),
        category: Yup.array(),
        name: Yup.string().nullable(),
        id: Yup.string().nullable(),
        sku: Yup.string().nullable(),
        priority: Yup.string().nullable(),
        createdDateFrom: Yup.date().nullable()
            .test("testCreatedDateFrom", t('columns.invalidDate'), (value, context) => {
                return (!value || !context.parent.createdDateTo || value <= context.parent.createdDateTo)
            })
            .typeError(t('columns.invalidDate')),
        createdDateTo: Yup.date().nullable()
            .test("testCreatedDateTo", t('columns.invalidDate'), (value, context) => {
                return (!value || !context.parent.createdDateFrom || value >= context.parent.createdDateFrom)
            })
            .typeError(t('columns.invalidDate')),
        lastModifiedDateFrom: Yup.date().nullable()
            .test("testLastModifiedDateFrom", t('columns.invalidDate'), (value, context) => {
                return (!value || !context.parent.lastModifiedDateTo || value <= context.parent.lastModifiedDateTo)
            })
            .typeError(t('columns.invalidDate')),
        lastModifiedDateTo: Yup.date().nullable()
            .test("testLastModifiedDateTo", t('columns.invalidDate'), (value, context) => {
                return (!value || !context.parent.lastModifiedDateFrom || value >= context.parent.lastModifiedDateFrom)
            })
            .typeError(t('columns.invalidDate'))
    }

    const renderName = (field: any) => (
        <TextField
            fullWidth
            label={t('columns.name')}
            value={field.value}
            onChange={field.onChange}
        />
    );
    const renderId = (field: any) => (
        <TextField
            fullWidth
            label={t('columns.id')}
            value={field.value}
            onChange={field.onChange}
        />
    );

    const renderSku = (field: any) => (
        <TextField
            fullWidth
            label={t('columns.sku')}
            value={field.value}
            onChange={field.onChange}
        />
    );

    const renderPriority = (field: any) => (
        <TextField
            fullWidth
            label={t('columns.priority')}
            value={field.value}
            onChange={(e: any) => {
                if (e.target.value.match(INTEGER_REG_EXP) || e.target.value === '')
                    field.onChange(e.target.value)
            }}
        />
    );

    const renderStatus = (field: any) => (
        <TextField
            fullWidth
            label={t('columns.status')}
            select
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.map((s: any) => statuses.find(ss => ss.value === s)!.label).join(', ')
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {statuses?.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                    <Checkbox checked={field.value.indexOf(status.value) > -1}/>
                    <span style={{
                        border: "1px solid " + status.color,
                        backgroundColor: status.color,
                        borderRadius: "50%",
                        display: "inline-block",
                        height: "14px",
                        width: "14px",
                        marginRight: "5px"
                    }}/>
                    <ListItemText primary={status.label}/>
                </MenuItem>
            ))}
        </TextField>
    );

    const renderCategory = (field: any, error: any, formState: any, values: any) => (
        <TextField
            fullWidth
            label={t('columns.category')}
            select
            disabled={!values.legalEntity || values.legalEntity.length === 0}
            SelectProps={{
                multiple: true,
                renderValue: (selected: any) => selected.join(', ')
            }}
            value={field.value}
            onChange={field.onChange}
        >
            {categories?.map((category) => (
                <MenuItem key={category.categoryCode} value={category.categoryName}>
                    <Checkbox checked={field.value.indexOf(category.categoryName) > -1}/>
                    <ListItemText primary={category.categoryLabel || category.categoryName}/>
                </MenuItem>
            ))}
        </TextField>
    );

    const renderCreatedDateFrom = (field: any, error: any, formState: any, values: any) => (
        <DateTimePicker
            label={t('columns.createdDateFrom')}
            value={field.value}
            maxDate={values["createdDateTo"] !== null ? values["createdDateTo"] : null}
            onChange={field.onChange}
            ampm={false}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    id={"createdDateFrom"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}
                />
            )}
            inputFormat={"DD/MM/YYYY HH:mm"}
        />
    )
    const renderCreatedDateTo = (field: any, error: any, formState: any, values: any) => (
        <DateTimePicker
            label={t('columns.createdDateTo')}
            value={field.value}
            minDate={values["createdDateFrom"] !== null ? values["createdDateFrom"] : null}
            onChange={field.onChange}
            ampm={false}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    id={"createdDateTo"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}
                />
            )}
            inputFormat={"DD/MM/YYYY HH:mm"}
        />
    )
    const renderLastModifiedDateFrom = (field: any, error: any, formState: any, values: any) => (
        <DateTimePicker
            label={t('columns.lastModifiedDateFrom')}
            value={field.value}
            maxDate={values["lastModifiedDateTo"] !== null ? values["lastModifiedDateTo"] : null}
            onChange={field.onChange}
            ampm={false}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    id={"lastModifiedDateFrom"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}
                />
            )}
            inputFormat={"DD/MM/YYYY HH:mm"}
        />
    )

    const renderLastModifiedDateTo = (field: any, error: any, formState: any, values: any) => (
        <DateTimePicker
            label={t('columns.lastModifiedDateTo')}
            value={field.value}
            minDate={values["lastModifiedDateFrom"] !== null ? values["lastModifiedDateFrom"] : null}
            onChange={field.onChange}
            ampm={false}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    id={"lastModifiedDateTo"}
                    error={error !== undefined}
                    helperText={error ? error.message : ""}
                />
            )}
            inputFormat={"DD/MM/YYYY HH:mm"}
        />
    )

    const fields = {
        status: renderStatus,
        category: renderCategory,
        name: renderName,
        id: renderId,
        createdDateFrom: renderCreatedDateFrom,
        createdDateTo: renderCreatedDateTo,
        lastModifiedDateFrom: renderLastModifiedDateFrom,
        lastModifiedDateTo: renderLastModifiedDateTo,
        sku: renderSku,
        priority: renderPriority,

    }

    return (
        <>
            <SectionHeader title={t('listHeader.title')}
                           subtitle={t('listHeader.subTitle')}/>

            <AdvancedFilter validationObj={validationObj} fields={fields} defaultValues={filterDefaultValues}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}/>
            <Grid container>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    <AgGridCustomTable
                        gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        onGridReady={(params) => {
                            params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                        }}
                    />
                </Grid>
            </Grid>
            <SubscriptionDetailModal open={openDetail} subscription={selectedSubscription} handleClose={() => {
                setOpenDetail(false)
            }}/>
        </>
    )
}
export default SubscriptionListPage;